import { C as Component } from "./component-CZdU8SYg.js";
import { V as VariantSelectedEvent, a as VariantUpdateEvent } from "./events-C4hlH4zR.js";
import { m as morph } from "./morph-zvPryG73.js";
import {
  d as defineCustomElement,
  f as formatMoney,
  u as updateGalleryMedia,
} from "./utilities-D68o91ht.js";
class VariantSelector extends Component {
  requiredRefs = [];
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {(event: Event) => void} */
  #boundOnVariantChange;
  connectedCallback() {
    super.connectedCallback();
    this.#boundOnVariantChange = this.onVariantChange.bind(this);
    this.addEventListener("change", this.#boundOnVariantChange);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.#boundOnVariantChange);
    this.#abortController?.abort();
  }
  async onVariantChange(event) {
    const changedElement = event.target;
    if (!changedElement.hasAttribute("data-option-selector")) {
      return;
    }
    const optionValueElement =
      changedElement.tagName === "SELECT"
        ? changedElement.options[changedElement.selectedIndex]
        : changedElement;
    const productSection = this.closest('[data-section-type="product"]');
    const sectionId = this.dataset.sectionId;
    const newProductUrl = optionValueElement.dataset.productUrl;
    const newVariantId = optionValueElement.dataset.variantId;
    const selectedElementId = optionValueElement.id;
    if (!newProductUrl || newProductUrl.trim() === "") {
      console.error("Product URL missing from variant option element");
      this.dispatchEvent(
        new CustomEvent("variant:error", {
          bubbles: true,
          detail: { error: new Error("Missing product URL"), variantId: newVariantId },
        })
      );
      return;
    }
    const price = parseInt(optionValueElement.dataset.price, 10);
    const compareAtPrice = optionValueElement.dataset.compareAtPrice
      ? parseInt(optionValueElement.dataset.compareAtPrice, 10)
      : null;
    const isAvailable = optionValueElement.dataset.available === "true";
    this.#updatePriceInstantly(productSection, price, compareAtPrice);
    this.#updateButtonInstantly(productSection, isAvailable);
    this.#updateGalleryInstantly(productSection, newVariantId);
    const variantInput = productSection?.querySelector("#variant-id-input");
    if (variantInput) {
      variantInput.value = newVariantId;
    }
    if (newVariantId) {
      const url = new URL(newProductUrl, window.location.origin);
      const params2 = new URLSearchParams(window.location.search);
      params2.set("variant", newVariantId);
      url.search = params2.toString();
      window.history.replaceState({}, "", url.toString());
    }
    this.#updateOptionLegendInstantly(selectedElementId);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: optionValueElement.dataset.optionValueId || "",
        variantId: newVariantId,
      })
    );
    let params = `section_id=${sectionId}&`;
    if (newVariantId) {
      params += `variant=${newVariantId}`;
    } else {
      const selectedOptionValues = Array.from(
        this.querySelectorAll(
          "[data-option-selector]:checked, [data-option-selector] option:checked"
        )
      )
        .map((element) => element.dataset.optionValueId)
        .filter(Boolean)
        .join(",");
      params += `option_values=${selectedOptionValues}`;
    }
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const response = await fetch(`${newProductUrl}?${params}`, {
        signal: this.#abortController.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const newProductSection = html.querySelector('[data-section-type="product"]');
      if (!newProductSection) {
        throw new Error("Product section not found in response");
      }
      const variantDataScript = newProductSection.querySelector('script[type="application/json"]');
      let variantData = null;
      if (variantDataScript) {
        try {
          variantData = JSON.parse(variantDataScript.textContent);
        } catch (parseError) {
          console.warn("Failed to parse variant data JSON:", parseError);
        }
      }
      const currentFieldsets = this.querySelectorAll("fieldset");
      const newFieldsets = newProductSection.querySelectorAll("variant-selector fieldset");
      currentFieldsets.forEach((fieldset, i) => {
        if (newFieldsets[i]) {
          morph(fieldset, newFieldsets[i]);
        }
      });
      let freshAvailability = isAvailable;
      const fetchedOptionElement = newProductSection.querySelector(
        `[data-option-selector][data-variant-id="${newVariantId}"], [data-option-selector][id="${selectedElementId}"]`
      );
      if (fetchedOptionElement) {
        freshAvailability = fetchedOptionElement.dataset.available === "true";
      }
      this.#updateButtonInstantly(productSection, freshAvailability);
      if (variantData) {
        this.dispatchEvent(
          new VariantUpdateEvent(variantData, optionValueElement.dataset.optionValueId || "", {
            html: newProductSection,
            productId: this.dataset.productId || "",
            newProduct: null,
          })
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error("Variant selector fetch error:", error.message);
      this.dispatchEvent(
        new CustomEvent("variant:error", {
          bubbles: true,
          detail: { error, variantId: newVariantId },
        })
      );
    }
  }
  /**
   * Update option legend text instantly
   * @param {string} selectedElementId
   */
  #updateOptionLegendInstantly(selectedElementId) {
    const selectedElement = document.getElementById(selectedElementId);
    if (!selectedElement) {
      return;
    }
    let selectedText = "";
    if (selectedElement.tagName === "INPUT") {
      selectedText = selectedElement.dataset.value || "";
    } else if (selectedElement.tagName === "OPTION") {
      selectedText = selectedElement.textContent.trim();
    }
    if (!selectedText) {
      return;
    }
    const fieldset = selectedElement.closest("fieldset");
    const strongTag = fieldset?.querySelector("legend strong");
    if (strongTag) {
      strongTag.textContent = selectedText;
    }
  }
  /**
   * Update price display instantly from cached data
   * @param {Element | null} productSection
   * @param {number} price - Price in cents
   * @param {number | null} compareAtPrice - Compare at price in cents
   */
  #updatePriceInstantly(productSection, price, compareAtPrice) {
    if (!productSection) {
      return;
    }
    const priceContainer = productSection.querySelector("#product-price");
    if (!priceContainer) {
      return;
    }
    const locale = window.Shopify?.locale || "de-DE";
    const currency = this.dataset.currency || "EUR";
    const formattedPrice = formatMoney(price, { locale, currency });
    const formattedCompareAt = compareAtPrice
      ? formatMoney(compareAtPrice, { locale, currency })
      : "";
    const priceElement = priceContainer.querySelector(".font-bold:not(.line-through)");
    const compareAtElement = priceContainer.querySelector(".line-through");
    if (priceElement) {
      priceElement.textContent = formattedPrice;
    }
    if (compareAtElement) {
      if (compareAtPrice && compareAtPrice > price) {
        compareAtElement.textContent = formattedCompareAt;
        compareAtElement.style.display = "";
      } else {
        compareAtElement.textContent = "";
        compareAtElement.style.display = "none";
      }
    }
  }
  /**
   * Update button state instantly from cached data
   * @param {Element | null} productSection
   * @param {boolean} isAvailable
   */
  #updateButtonInstantly(productSection, isAvailable) {
    if (!productSection) {
      return;
    }
    const button = productSection.querySelector("#product-buy-buttons button[type='submit']");
    if (!button) {
      return;
    }
    button.disabled = !isAvailable;
    const buttonText = button.querySelector(".button-text");
    if (buttonText) {
      buttonText.textContent = isAvailable
        ? window.Theme?.translations?.products?.addToCart || "Add to Cart"
        : window.Theme?.translations?.products?.soldOut || "Sold Out";
    }
  }
  /**
   * Update gallery instantly via CSS show/hide
   * Toggle .media-hidden class based on position ranges from variant mapping
   * @param {Element | null} productSection
   * @param {string} variantId
   */
  #updateGalleryInstantly(productSection, variantId) {
    if (!productSection) {
      return;
    }
    const gallery = productSection.querySelector(".product-media-gallery");
    if (!gallery) {
      return;
    }
    gallery.dataset.variantId = variantId;
    const positionsScript = gallery.querySelector("script[data-variant-positions]");
    if (!positionsScript) {
      return;
    }
    let variantPositions;
    try {
      variantPositions = JSON.parse(positionsScript.textContent);
    } catch {
      return;
    }
    const range = variantPositions[variantId];
    if (!range) {
      return;
    }
    const { start, end } = range;
    updateGalleryMedia(gallery, start, end);
  }
}
defineCustomElement("variant-selector", VariantSelector);
